/**
 * Static-page SEO metadata — the single source of truth shared by the client
 * (SeoHead on each page) and the API server's crawler tag injection.
 *
 * This file exists in two byte-identical copies:
 *   artifacts/tubedealer/src/shared/staticPageMeta.ts
 *   artifacts/api-server/src/shared/staticPageMeta.ts
 * A test in the API server (staticMetaParity.test.ts) fails if the copies
 * ever drift, so edit BOTH files together.
 *
 * `title` is the FINAL <title> text — no "| TubeDealer" suffix is appended
 * by the generator. Keep every title at 60 characters or fewer.
 * Keep descriptions roughly 140–160 characters.
 */
export interface StaticPageMeta {
  title: string;
  description: string;
  canonical: string;
}

export const STATIC_PAGE_META: Record<string, StaticPageMeta> = {
  "/": {
    title:       "TubeDealer Marketplace — Buy & Sell Vacuum Tubes",
    description: "TubeDealer is the marketplace for modern production and vintage NOS vacuum tubes. Import supplier CSVs, scan inventory with AI, and sell across channels.",
    canonical:   "https://tubedealer.com/",
  },
  "/browse": {
    title:       "Browse Vacuum Tubes for Sale | TubeDealer",
    description: "Shop modern production and vintage NOS vacuum tubes from verified dealers. Filter by tube type, brand, or condition. No account required.",
    canonical:   "https://tubedealer.com/browse",
  },
  "/gear": {
    title:       "Find Tubes for Your Amp or Gear | TubeDealer",
    description: "Find the right replacement tubes for your amp. Search by amplifier brand and model to see the exact tube complement and available stock on TubeDealer.",
    canonical:   "https://tubedealer.com/gear",
  },
  "/quick-start": {
    title:       "Quick Start: List Your First Tube | TubeDealer",
    description: "Get your first vacuum tube listing live on TubeDealer in under 15 minutes — six steps from account creation to your first published marketplace listing.",
    canonical:   "https://tubedealer.com/quick-start",
  },
  "/user-guide": {
    title:       "TubeDealer User Guide — Complete Documentation",
    description: "The complete TubeDealer user guide covering tube scanning, inventory management, eBay and Reverb sync, marketplace listings, fulfillment, and storefronts.",
    canonical:   "https://tubedealer.com/user-guide",
  },
  "/support": {
    title:       "Contact & Support | TubeDealer",
    description: "Get help with your TubeDealer account. Submit a support ticket or browse common questions. Our team typically responds within one business day.",
    canonical:   "https://tubedealer.com/support",
  },
  "/amps": {
    title:       "Amp Tube Guides by Brand | TubeDealer",
    description: "Browse tube complement guides for every major amplifier brand — Fender, Marshall, Vox, Mesa/Boogie, Ampeg, and more. Find tested replacement tubes for your amp.",
    canonical:   "https://tubedealer.com/amps",
  },
  "/about": {
    title:       "About TubeDealer — Our Story & FAQ | TubeDealer",
    description: "Learn about TubeDealer's story and how dealers manage vintage NOS and modern production vacuum tubes with CSV imports, scanning, and multi-channel sales tools.",
    canonical:   "https://tubedealer.com/about",
  },
  "/terms": {
    title:       "Terms of Service | TubeDealer",
    description: "TubeDealer's terms of service — marketplace rules, seller obligations, buyer protections, and platform policies for the vacuum tube marketplace.",
    canonical:   "https://tubedealer.com/terms",
  },
};
